import React from "react";
import { getNews } from "../api/newsApi";
import NewsCard from "../components/NewsCard";

const News = () => {
  const [newsList, setNewsList] = React.useState([]);
  // For mobile/tablet single card view
  const [currentIdx, setCurrentIdx] = React.useState(0);
  // Responsive check for mobile/tablet
  const [isSingleCard, setIsSingleCard] = React.useState(false);
  React.useEffect(() => {
    const checkScreen = () => {
      setIsSingleCard(window.innerWidth < 1024); // <1024px = mobile/tablet
    };
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);
  // Mobile/Tablet swipe, mouse drag, and arrow key navigation
  const pointerStartX = React.useRef(null);
  const pointerEndX = React.useRef(null);

  // Touch events
  const handleTouchStart = (e) => {
    pointerStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e) => {
    pointerEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    if (pointerStartX.current === null || pointerEndX.current === null) return;
    const diff = pointerStartX.current - pointerEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        setCurrentIdx((prev) => (prev < newsList.length - 1 ? prev + 1 : 0));
      } else {
        setCurrentIdx((prev) => (prev > 0 ? prev - 1 : newsList.length - 1));
      }
    }
    pointerStartX.current = null;
    pointerEndX.current = null;
  };

  // Mouse events
  const handleMouseDown = (e) => {
    pointerStartX.current = e.clientX;
  };
  const handleMouseMove = (e) => {
    if (pointerStartX.current !== null) {
      pointerEndX.current = e.clientX;
    }
  };
  const handleMouseUp = () => {
    if (pointerStartX.current === null || pointerEndX.current === null) return;
    const diff = pointerStartX.current - pointerEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        setCurrentIdx((prev) => (prev < newsList.length - 1 ? prev + 1 : 0));
      } else {
        setCurrentIdx((prev) => (prev > 0 ? prev - 1 : newsList.length - 1));
      }
    }
    pointerStartX.current = null;
    pointerEndX.current = null;
  };

  // Arrow key navigation for single card view
  React.useEffect(() => {
    if (!isSingleCard) return;
    const handleArrow = (e) => {
      if (e.key === "ArrowLeft") {
        setCurrentIdx((prev) => (prev > 0 ? prev - 1 : newsList.length - 1));
      } else if (e.key === "ArrowRight") {
        setCurrentIdx((prev) => (prev < newsList.length - 1 ? prev + 1 : 0));
      }
    };
    window.addEventListener("keydown", handleArrow);
    return () => window.removeEventListener("keydown", handleArrow);
  }, [isSingleCard, newsList.length]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [modalNews, setModalNews] = React.useState(null);
  const scrollContainerRef = React.useRef(null);

  const handleSeeMoreNews = (news) => setModalNews(news);
  const closeModalNews = () => setModalNews(null);

  // Handle backdrop click to close modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModalNews();
    }
  };

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === "Escape" && modalNews) {
        closeModalNews();
      }
    };

    if (modalNews) {
      document.addEventListener("keydown", handleEscapeKey);
      return () => document.removeEventListener("keydown", handleEscapeKey);
    }
  }, [modalNews]);

  // Scroll navigation functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -400, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 400, behavior: "smooth" });
    }
  };

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyPress = (e) => {
      if (modalNews) return; // Don't navigate if modal is open

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        scrollLeft();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        scrollRight();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [modalNews]);

  React.useEffect(() => {
    let isMounted = true;
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await getNews();
        if (!response.success) return;
        if (isMounted) setNewsList(response.data);
      } catch (err) {
        if (isMounted) setError("Failed to load news. Please try again later.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchNews();
    return () => {
      isMounted = false;
    };
  }, []);

  // Prevent background scroll when modal is open
  React.useEffect(() => {
    if (modalNews) {
      document.body.classList.add("modal-open");
      // Prevent scrolling on the body
      const scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
    } else {
      document.body.classList.remove("modal-open");
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.top = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }

    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.top = "";
    };
  }, [modalNews]);

  // Helper function to get a usable image URL (thumbnail for Drive links)
  const getImageUrl = (url) => {
    if (!url) return "/news/news1.png";

    // Google Drive link pattern
    const driveMatch = url.match(
      /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/?.*/
    );
    if (driveMatch && driveMatch[1]) {
      // Use Google Drive thumbnail
      return `https://drive.google.com/thumbnail?id=${driveMatch[1]}`;
    }

    return url;
  };

  // pagination logic

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      {/* Full-page animated/gradient background */}
      <div
        className="fixed inset-0 -z-10 bg-gradient-to-br from-blue-200 via-white to-blue-400 dark:from-gray-900 dark:via-gray-950 dark:to-blue-900 animate-fadein"
        aria-hidden="true"
      />
      {/* Main content */}
      <div className="min-h-screen bg-transparent transition-all duration-500">
        {/* Header Section */}
        <section className="pt-24 pb-6 mb-4 relative">
          <div className="absolute inset-0 pointer-events-none select-none">
            <div className="w-full h-full bg-gradient-to-tr from-blue-200/60 via-blue-100/30 to-transparent dark:from-blue-900/40 dark:via-gray-900/30 dark:to-transparent blur-2xl animate-fadein" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-black tracking-tight drop-shadow-lg animate-fadein font-manrope">
                News & Updates
              </h1>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-6 relative z-10">
          {loading && (
            <div className="text-center text-blue-500 py-8 animate-pulse">
              Loading news...
            </div>
          )}
          {error && !loading && (
            <div className="text-center text-red-600 py-8 animate-fadein">
              {error}
            </div>
          )}
          {!loading && !error && newsList.length === 0 && (
            <div className="text-center text-gray-500 py-8 animate-fadein">
              No news found.
            </div>
          )}

          {/* News Grid */}
          {!loading && !error && newsList.length > 0 && (
            <>
              {/* Swipe Instruction */}
              <div className="text-center mb-4">
                <div className="inline-flex items-center bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-4 py-2 rounded-full text-sm font-medium shadow-sm">
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M7 16l-4-4m0 0l4-4m-4 4h18"
                    ></path>
                  </svg>
                  <span className="hidden lg:inline">
                    Use arrow keys or swipe left/right to see more news
                  </span>
                  <span className="hidden sm:inline lg:hidden">
                    Swipe left/right or click arrows to see more news
                  </span>
                  <span className="sm:hidden">Swipe to see more news</span>
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    ></path>
                  </svg>
                </div>
              </div>

              {/* Navigation Arrows - Positioned Outside Cards */}
              <div className="relative max-w-7xl mx-auto mb-2">
                {/* Left Navigation Arrow */}
                <button
                  onClick={scrollLeft}
                  className="absolute top-1/2 -left-12 lg:-left-16 z-30 hidden lg:flex items-center justify-center w-10 h-10 lg:w-14 lg:h-14 bg-white/95 hover:bg-white nav-arrow-shadow rounded-full text-blue-600 hover:text-blue-700 transition-all duration-300 backdrop-blur-md border border-white/20 hover:scale-110 group focus:outline-none focus:ring-4 focus:ring-blue-300/50"
                  style={{ transform: "translateY(calc(-50% + 200px))" }}
                  aria-label="Previous news (Left arrow key)"
                  title="Previous news (Left arrow key)"
                >
                  <svg
                    className="w-5 h-5 lg:w-7 lg:h-7 group-hover:animate-pulse"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                {/* Right Navigation Arrow */}
                <button
                  onClick={scrollRight}
                  className="absolute top-1/2 -right-12 lg:-right-16 z-30 hidden lg:flex items-center justify-center w-10 h-10 lg:w-14 lg:h-14 bg-white/95 hover:bg-white nav-arrow-shadow rounded-full text-blue-600 hover:text-blue-700 transition-all duration-300 backdrop-blur-md border border-white/20 hover:scale-110 group focus:outline-none focus:ring-4 focus:ring-blue-300/50"
                  style={{ transform: "translateY(calc(-50% + 200px))" }}
                  aria-label="Next news (Right arrow key)"
                  title="Next news (Right arrow key)"
                >
                  <svg
                    className="w-5 h-5 lg:w-7 lg:h-7 group-hover:animate-pulse"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>

              {/* News Cards Responsive Layout */}
              {isSingleCard ? (
                <div className="relative flex flex-col items-center w-full">
                  <div
                    className="flex items-center justify-center w-full select-none"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{ touchAction: 'pan-y', cursor: 'grab' }}
                  >
                    <div className="w-72 sm:w-80 md:w-96">
                      <NewsCard
                        news={{ ...newsList[currentIdx], imageUrl: getImageUrl(newsList[currentIdx]?.imageUrl) }}
                        onClick={handleSeeMoreNews}
                      />
                    </div>
                  </div>
                  {/* Dots indicator */}
                  <div className="flex justify-center mt-3 space-x-2">
                    {newsList.map((_, idx) => (
                      <button
                        key={idx}
                        className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${currentIdx === idx ? 'bg-blue-500' : 'bg-gray-300'}`}
                        onClick={() => setCurrentIdx(idx)}
                        aria-label={`Go to news ${idx + 1}`}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="relative w-full bg-transparent">
                  <div
                    ref={scrollContainerRef}
                    className="news-scroll-container overflow-x-auto flex gap-4 sm:gap-6 snap-x snap-mandatory px-4 sm:px-6 pb-6 scrollbar-none smooth-scroll"
                  >
                    {newsList.map((news, idx) => (
                      <div
                        key={idx}
                        className="snap-start flex-none w-72 sm:w-80 md:w-96 lg:w-[400px]"
                      >
                        <NewsCard
                          news={{ ...news, imageUrl: getImageUrl(news.imageUrl) }}
                          onClick={handleSeeMoreNews}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Mobile Touch Indicator */}
              <div className="md:hidden text-center mt-4">
                <div className="inline-flex items-center text-gray-500 text-xs">
                  <div className="flex space-x-1 mr-2">
                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse"></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"
                      style={{ animationDelay: "0.2s" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-gray-300 rounded-full animate-pulse"
                      style={{ animationDelay: "0.4s" }}
                    ></div>
                  </div>
                  Swipe to navigate
                </div>
              </div>
            </>
          )}

          {/* Modal Popup for News Details */}
          {modalNews && (
            <div
              className="news-modal fixed inset-0 z-40 bg-black/60 backdrop-blur-md animate-fadein"
              onClick={handleBackdropClick}
            >
              <div className="h-full overflow-y-auto flex items-start justify-center pt-20 pb-8 px-4">
                <div
                  className="modal-container bg-white rounded-3xl shadow-2xl max-w-4xl w-full overflow-hidden relative border border-gray-200 animate-fadein my-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Close Button */}
                  <button
                    onClick={closeModalNews}
                    aria-label="Close"
                    className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20 text-gray-600 hover:text-gray-800 text-xl sm:text-2xl font-bold bg-white/95 backdrop-blur-sm rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400 hover:bg-white hover:scale-105 transition-all duration-200"
                  >
                    ✕
                  </button>

                  {/* Header Image */}
                  <div className="relative h-48 sm:h-64 md:h-80 w-full overflow-hidden rounded-t-3xl">
                    <img
                      src={getImageUrl(modalNews.imageUrl)}
                      alt={modalNews.title}
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/news/news1.png";
                      }}
                      className="w-full h-full object-cover"
                    />
                    {/* Gradient overlay for better text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>

                    {/* Title overlay on image */}
                    <div className="absolute bottom-3 left-3 right-12 sm:bottom-6 sm:left-6 sm:right-16">
                      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2 leading-tight drop-shadow-lg">
                        {modalNews.title}
                      </h2>
                      {modalNews.publishedAt && (
                        <p className="text-white/90 text-xs sm:text-sm font-medium">
                          📅 {modalNews.publishedAt}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-4 sm:p-6 lg:p-8">
                    {/* Tags/Category */}
                    <div className="flex items-center mb-4 sm:mb-6">
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                        📰 News Update
                      </span>
                    </div>

                    {/* Main Content */}
                    <div className="prose prose-sm sm:prose-lg max-w-none">
                      <p className="text-gray-700 text-sm sm:text-lg leading-relaxed mb-4 sm:mb-6">
                        {modalNews.description}
                      </p>

                      {modalNews.content && (
                        <>
                          <div className="border-t border-gray-200 pt-4 mt-4 sm:pt-6 sm:mt-6">
                            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                              📝 Full Article
                            </h3>
                            <p className="text-gray-700 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                              {modalNews.content}
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-center mt-6 pt-4 border-t border-gray-200 sm:mt-8 sm:pt-6">
                      <button
                        onClick={closeModalNews}
                        className="flex items-center justify-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-6 py-2 sm:px-8 sm:py-3 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base"
                      >
                        <span>Close</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default News;
